/** Host OS — Customer detail. Full CRM profile: contact, lifetime value,
 *  memberships, booking timeline, team-visible notes, tags & lifecycle status. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Calendar, Repeat, Plane, Mail, Phone, MapPin,
  Clock, Trash2, Plus, X, StickyNote, Wallet, BadgeCheck,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { KpiCard, formatMoneyMap, TypeBadge } from "@/components/host/hostUi";
import { StatusPill, STATUS_OPTIONS } from "@/components/host/crmUi";

const initials = (n) => (!n ? "?" : n.split(" ").map((x) => x[0]).join("").toUpperCase().slice(0, 2));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const bookingIcon = { game: Calendar, series: Repeat, trip: Plane };

const HostCustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [noteText, setNoteText] = useState("");
  const [postingNote, setPostingNote] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/crm/customers/${id}`, { headers });
      if (res.ok) setC(await res.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const patch = async (body, okMsg) => {
    const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
    const res = await fetch(`${API}/host/crm/customers/${id}`, { method: "PATCH", headers, body: JSON.stringify(body) });
    if (!res.ok) { toast.error("Update failed"); return false; }
    if (okMsg) toast.success(okMsg);
    return true;
  };

  const changeStatus = async (status) => {
    setSavingStatus(true);
    if (await patch({ status })) setC((p) => ({ ...p, status, status_manual: status }));
    setSavingStatus(false);
  };

  const addTag = async () => {
    const t = newTag.trim();
    if (!t) return;
    const tags = Array.from(new Set([...(c.tags || []), t]));
    if (await patch({ tags })) { setC((p) => ({ ...p, tags })); setNewTag(""); }
  };

  const removeTag = async (t) => {
    const tags = (c.tags || []).filter((x) => x !== t);
    if (await patch({ tags })) setC((p) => ({ ...p, tags }));
  };

  const saveContact = async (field, value) => {
    await patch({ [field]: value });
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setPostingNote(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/host/crm/customers/${id}/notes`, { method: "POST", headers, body: JSON.stringify({ text: noteText }) });
      if (!res.ok) throw new Error();
      const { note } = await res.json();
      setC((p) => ({ ...p, notes: [note, ...(p.notes || [])] }));
      setNoteText("");
    } catch { toast.error("Couldn't add note"); } finally { setPostingNote(false); }
  };

  const deleteNote = async (noteId) => {
    const headers = await authHeaders();
    const res = await fetch(`${API}/host/crm/customers/${id}/notes/${noteId}`, { method: "DELETE", headers });
    if (res.ok) setC((p) => ({ ...p, notes: (p.notes || []).filter((n) => n.note_id !== noteId) }));
  };

  if (loading) {
    return <HostLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></HostLayout>;
  }
  if (!c) {
    return <HostLayout><Card className="p-10 text-center">Customer not found.</Card></HostLayout>;
  }

  return (
    <HostLayout>
      <div data-testid="customer-detail-page">
        <button onClick={() => navigate("/host/customers")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="customer-back-btn">
          <ArrowLeft className="w-4 h-4" /> All customers
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <Avatar className="w-16 h-16 ring-1 ring-border">
            <AvatarImage src={c.picture} alt={c.name} />
            <AvatarFallback className="bg-primary/20 text-primary-ink text-lg">{initials(c.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl leading-tight">{c.name}</h1>
              <StatusPill status={c.status} />
              {c.source === "manual" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase tracking-wide">Manual</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Customer since {fmtDate(c.first_at)} · Last activity {fmtDate(c.last_at)}
            </p>
          </div>
          <div className="w-40 shrink-0">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lifecycle</label>
            <Select value={c.status} onValueChange={changeStatus} disabled={savingStatus}>
              <SelectTrigger className="mt-1" data-testid="customer-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Submenu tabs */}
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="w-full justify-start gap-1 h-auto p-0 bg-transparent border-b border-border rounded-none mb-6" data-testid="customer-tabs">
            {[
              { v: "summary", label: "Summary" },
              { v: "reservations", label: "Reservations" },
              { v: "notes", label: "Notes" },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                className="rounded-md px-4 py-2 text-sm data-[state=active]:bg-muted data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground -mb-px"
                data-testid={`customer-tab-${t.v}`}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ---- Summary ---- */}
          <TabsContent value="summary" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <KpiCard label="Lifetime value" value={formatMoneyMap(c.ltv_by_currency)} icon={Wallet} accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" />
              <KpiCard label="Events booked" value={c.events_total} sub={`${c.counts.games} games · ${c.counts.series} series · ${c.counts.trips} trips`} icon={Calendar} />
              <KpiCard label="Memberships" value={(c.memberships || []).filter((m) => m.status === "active").length} icon={BadgeCheck} accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" />
              <KpiCard label="Upcoming" value={(c.upcoming || []).length} icon={Clock} accent="bg-blue-500/15 text-blue-700 dark:text-blue-300" />
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {/* Contact */}
              <Card className="p-4" data-testid="customer-contact-card">
                <h3 className="font-medium text-sm mb-3">Contact</h3>
                <div className="space-y-3">
                  <ContactField icon={Mail} label="Email" value={c.email} onSave={(v) => saveContact("email", v)} />
                  <ContactField icon={Phone} label="Phone" value={c.phone} onSave={(v) => saveContact("phone", v)} />
                  <ContactField icon={MapPin} label="City" value={c.city} onSave={(v) => saveContact("city", v)} />
                </div>
              </Card>

              {/* Tags */}
              <Card className="p-4" data-testid="customer-tags-card">
                <h3 className="font-medium text-sm mb-3">Tags</h3>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(c.tags || []).length === 0 && <p className="text-xs text-muted-foreground">No tags yet.</p>}
                  {(c.tags || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-muted text-foreground" data-testid={`customer-tag-${t}`}>
                      #{t}
                      <button onClick={() => removeTag(t)} className="hover:text-destructive" data-testid={`remove-tag-${t}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="Add a tag (e.g. VIP, Pilates)" className="h-9" data-testid="customer-tag-input" />
                  <Button size="sm" onClick={addTag} className="h-9" data-testid="customer-tag-add"><Plus className="w-4 h-4" /></Button>
                </div>
              </Card>

              {/* Memberships */}
              {(c.memberships || []).length > 0 && (
                <Card className="p-4 lg:col-span-2" data-testid="customer-memberships-card">
                  <h3 className="font-medium text-sm mb-3">Memberships</h3>
                  <div className="space-y-2">
                    {c.memberships.map((m, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="truncate">{m.title}</span>
                        <span className="text-xs text-muted-foreground shrink-0 ml-2">
                          {m.status === "active" ? `${m.sessions_remaining ?? "—"}/${m.sessions_total ?? "—"} left` : "Cancelled"}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Booking history */}
              <Card className="p-4 lg:col-span-2" data-testid="customer-summary-bookings-card">
                <h3 className="font-medium text-sm mb-3">Booking history</h3>
                {(c.bookings || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No bookings recorded with you yet.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {c.bookings.map((b, i) => <BookingRow key={i} b={b} idx={i} />)}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ---- Reservations ---- */}
          <TabsContent value="reservations" className="mt-0 space-y-6" data-testid="customer-reservations-tab">
            <div>
              <h3 className="font-display text-lg mb-3">Upcoming reservations</h3>
              <Card className="p-4">
                {(c.upcoming || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No upcoming reservations</p>
                ) : (
                  <div className="divide-y divide-border">
                    {c.upcoming.map((b, i) => <BookingRow key={i} b={b} idx={i} />)}
                  </div>
                )}
              </Card>
            </div>

            <div>
              <h3 className="font-display text-lg mb-3">Booking history</h3>
              <Card className="p-4">
                {(c.bookings || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No bookings recorded with you yet.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {c.bookings.map((b, i) => <BookingRow key={i} b={b} idx={i} />)}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ---- Notes ---- */}
          <TabsContent value="notes" className="mt-0" data-testid="customer-notes-tab">
            <Card className="p-4" data-testid="customer-notes-card">
              <h3 className="font-medium text-sm mb-3 flex items-center gap-1.5"><StickyNote className="w-4 h-4" /> Team notes</h3>
              <div className="flex gap-2 mb-4">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add a note visible to your whole team…" data-testid="customer-note-input" />
                <Button onClick={addNote} disabled={postingNote} className="shrink-0 self-end" data-testid="customer-note-add">
                  {postingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post"}
                </Button>
              </div>
              <div className="space-y-3">
                {(c.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet — jot down anything the team should know.</p>}
                {(c.notes || []).map((n) => (
                  <div key={n.note_id} className="group flex items-start gap-2 text-sm border-l-2 border-border pl-3" data-testid={`note-${n.note_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="whitespace-pre-wrap break-words">{n.text}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{n.author_name} · {fmtDateTime(n.created_at)}</p>
                    </div>
                    <button onClick={() => deleteNote(n.note_id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`note-delete-${n.note_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </HostLayout>
  );
};

const BookingRow = ({ b, idx }) => {
  const Icon = bookingIcon[b.type] || Calendar;
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`booking-${idx}`}>
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{b.title}</p>
          <TypeBadge type={b.type} />
        </div>
        <p className="text-[11px] text-muted-foreground">Booked {fmtDate(b.when)}{b.event_date ? ` · Event ${fmtDate(b.event_date)}` : ""}</p>
      </div>
      <p className="text-sm font-semibold shrink-0">{b.amount > 0 ? formatMoneyMap({ [b.currency]: b.amount }) : "Free"}</p>
    </div>
  );
};

const ContactField = ({ icon: Icon, label, value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        {editing ? (
          <div className="flex gap-1.5 mt-1">
            <Input value={v} onChange={(e) => setV(e.target.value)} className="h-8" data-testid={`contact-input-${label.toLowerCase()}`} />
            <Button size="sm" className="h-8" onClick={() => { onSave(v); setEditing(false); }} data-testid={`contact-save-${label.toLowerCase()}`}>Save</Button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-sm text-left hover:text-primary-ink truncate w-full" data-testid={`contact-value-${label.toLowerCase()}`}>
            {value || <span className="text-muted-foreground italic">Add {label.toLowerCase()}</span>}
          </button>
        )}
      </div>
    </div>
  );
};

export default HostCustomerDetailPage;
